import { useSession } from "@/lib/auth-client";
import { Link, useRouterState } from "@tanstack/react-router";
import {
	Bell,
	CloudOff,
	FileText,
	Link2,
	type LucideIcon,
	Receipt,
	ShieldCheck,
	User,
	Users,
} from "lucide-react";

type NavItem = { label: string; to: string; icon: LucideIcon };
type NavGroup = { label: string; items: NavItem[]; adminOnly?: boolean };

const accountGroup: NavGroup = {
	label: "Account",
	items: [
		{ label: "Profile", to: "/settings/profile", icon: User },
		{ label: "Security", to: "/settings/security", icon: ShieldCheck },
		{ label: "Notifications", to: "/settings/notifications", icon: Bell },
	],
};

const workspaceGroup: NavGroup = {
	label: "Workspace",
	adminOnly: true,
	items: [
		{ label: "Team", to: "/settings/team", icon: Users },
		{ label: "QuickBooks", to: "/settings/quickbooks", icon: Link2 },
		{ label: "Invoicing", to: "/settings/invoicing", icon: Receipt },
		{ label: "Customer Mapping", to: "/settings/customer-mapping", icon: FileText },
		{ label: "Danger Zone", to: "/settings/danger-zone", icon: CloudOff },
	],
};

export function SettingsLayout({ children }: { children: React.ReactNode }) {
	const { data: sessionData } = useSession();
	const isAdmin = sessionData?.user?.role === "admin";
	const routerState = useRouterState();
	const currentPath = routerState.location.pathname;

	const groups = [accountGroup, ...(isAdmin ? [workspaceGroup] : [])];

	return (
		<div className="flex gap-8">
			<aside className="w-56 shrink-0">
				<div className="mb-4">
					<h1 className="text-xl font-semibold text-[#1e1e20]">Settings</h1>
					<p className="mt-1 text-sm text-[#6b7280]">
						Manage your account and workspace preferences.
					</p>
				</div>
				<nav className="space-y-6">
					{groups.map((group) => (
						<div key={group.label}>
							<div className="px-3 pb-1.5 text-[10px] font-medium uppercase tracking-[0.08em] text-[#9ca3af]">
								{group.label}
							</div>
							<div className="space-y-0.5">
								{group.items.map((item) => {
									const isActive = currentPath === item.to;
									const Icon = item.icon;
									return (
										<Link
											key={item.to}
											to={item.to}
											className={`relative flex items-center gap-2.5 rounded-md px-3 py-2 text-[13px] font-medium transition-colors duration-150 ${
												isActive
													? "bg-[#eef2ff] text-[#4f46e5]"
													: "text-[#6b7280] hover:bg-[#f3f4f6] hover:text-[#1e1e20]"
											}`}
										>
											{isActive && (
												<span className="absolute left-0 top-1.5 bottom-1.5 w-[3px] rounded-r bg-[#4f46e5]" />
											)}
											<Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={1.5} />
											{item.label}
										</Link>
									);
								})}
							</div>
						</div>
					))}
				</nav>
			</aside>

			<div className="min-w-0 flex-1 space-y-6">{children}</div>
		</div>
	);
}
